import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
	getAdminPlanCreationErrors,
	type PlanCreationErrorCategory,
	type PlanCreationErrorItem
} from '@services/adminActivity';
import { useAlertDialog } from '../../shared/AlertDialog';

type CategoryFilter = 'all' | PlanCreationErrorCategory;

const CATEGORY_TABS: Array<{ value: CategoryFilter; label: string }> = [
	{ value: 'all', label: 'All' },
	{ value: 'validation', label: 'Validation' },
	{ value: 'upload', label: 'Upload' },
	{ value: 'analysis', label: 'Garden analysis' },
	{ value: 'generation', label: 'Plan generation' },
	{ value: 'plan_flow', label: 'Plan flow' }
];

const DAY_OPTIONS = [7, 14, 30, 90] as const;

function formatEventLabel(eventName: string): string {
	return eventName.replace(/^plan_flow_/, 'flow: ').replace(/_/g, ' ');
}

function formatUserLabel(item: PlanCreationErrorItem): string {
	if (item.user_name || item.user_email) {
		return [item.user_name, item.user_email].filter(Boolean).join(' · ');
	}
	if (item.user_id) return `User #${item.user_id}`;
	return item.is_logged_in ? 'Logged-in user (unknown)' : 'Anonymous visitor';
}

export default function AdminErrors() {
	const { showAlert } = useAlertDialog();
	const [items, setItems] = useState<PlanCreationErrorItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [days, setDays] = useState<(typeof DAY_OPTIONS)[number]>(14);
	const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
	const [searchQuery, setSearchQuery] = useState('');
	const [offset, setOffset] = useState(0);
	const [total, setTotal] = useState(0);
	const [expandedId, setExpandedId] = useState<number | null>(null);
	const limit = 50;

	const categoryParam = categoryFilter === 'all' ? undefined : categoryFilter;

	useEffect(() => {
		setLoading(true);
		getAdminPlanCreationErrors({
			days,
			category: categoryParam,
			q: searchQuery.trim() || undefined,
			limit,
			offset
		})
			.then((payload) => {
				setItems(payload.items);
				setTotal(payload.total);
			})
			.catch((err) => {
				console.error('Failed to load plan creation errors:', err);
				showAlert(err instanceof Error ? err.message : 'Failed to load errors.', { title: 'Error' });
				setItems([]);
				setTotal(0);
			})
			.finally(() => setLoading(false));
	}, [days, categoryParam, searchQuery, offset, showAlert]);

	const pageStart = total === 0 ? 0 : offset + 1;
	const pageEnd = Math.min(offset + items.length, total);
	const canPrev = offset > 0;
	const canNext = offset + limit < total;

	const topEvents = useMemo(() => {
		const counts = new Map<string, number>();
		for (const item of items) {
			counts.set(item.event_name, (counts.get(item.event_name) ?? 0) + 1);
		}
		return [...counts.entries()]
			.sort((a, b) => b[1] - a[1])
			.slice(0, 5);
	}, [items]);

	return (
		<>
			<div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-4">
				<div>
					<h1 className="h3 mb-1">Errors</h1>
					<p className="text-muted mb-0">
						Detailed plan-creation failures for debugging. Full admin only.
					</p>
				</div>
				<Link to="/app/admin/analytics" className="text-decoration-none small" style={{ color: '#2f9e59' }}>
					← Admin Insights
				</Link>
			</div>

			<div className="mb-3 d-flex flex-wrap align-items-center gap-2">
				<span className="text-muted small">Window:</span>
				{DAY_OPTIONS.map((option) => (
					<button
						key={option}
						type="button"
						className={`btn btn-sm ${days === option ? 'btn-success' : 'btn-outline-secondary'}`}
						onClick={() => {
							setOffset(0);
							setDays(option);
						}}
					>
						{option}d
					</button>
				))}
			</div>

			<div className="mb-3 d-flex flex-wrap align-items-center gap-2">
				<span className="text-muted small">Stage:</span>
				{CATEGORY_TABS.map((category) => (
					<button
						key={category.value}
						type="button"
						className={`btn btn-sm ${categoryFilter === category.value ? 'btn-primary' : 'btn-outline-secondary'}`}
						onClick={() => {
							setOffset(0);
							setCategoryFilter(category.value);
						}}
					>
						{category.label}
					</button>
				))}
			</div>

			<div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
				<input
					type="search"
					className="form-control form-control-sm"
					placeholder="Search user name, email, or user id..."
					value={searchQuery}
					onChange={(e) => {
						setOffset(0);
						setSearchQuery(e.target.value);
					}}
					style={{ maxWidth: 360 }}
				/>
				{searchQuery && (
					<button
						type="button"
						className="btn btn-sm btn-outline-secondary"
						onClick={() => {
							setOffset(0);
							setSearchQuery('');
						}}
					>
						Clear
					</button>
				)}
				<span className="text-muted small ms-auto">
					{total === 0 ? 'No errors' : `Showing ${pageStart}–${pageEnd} of ${total}`}
				</span>
			</div>

			{!loading && topEvents.length > 0 && (
				<div className="alert alert-light border small mb-3">
					<strong className="me-2">On this page:</strong>
					{topEvents.map(([eventName, count], index) => (
						<span key={eventName}>
							{index > 0 ? ' · ' : ''}
							{formatEventLabel(eventName)} ({count})
						</span>
					))}
				</div>
			)}

			{loading ? (
				<div className="text-muted">Loading…</div>
			) : items.length === 0 ? (
				<div className="card">
					<div className="card-body text-center text-muted py-5">
						No plan-creation errors match the current filters.
					</div>
				</div>
			) : (
				<div className="card">
					<ul className="list-group list-group-flush">
						{items.map((item) => {
							const expanded = expandedId === item.id;
							return (
								<li key={item.id} className="list-group-item">
									<div className="d-flex flex-column flex-lg-row gap-3">
										<div className="flex-grow-1 min-w-0">
											<div className="d-flex flex-wrap align-items-center gap-2 mb-1">
												<span className="badge bg-danger-subtle text-danger-emphasis border border-danger-subtle">
													{formatEventLabel(item.event_name)}
												</span>
												<span className="text-muted small">
													{new Date(item.created_at).toLocaleString()}
												</span>
												{item.page_path && (
													<span className="text-muted small">{item.page_path}</span>
												)}
											</div>
											<div className="fw-semibold mb-1 text-break">{item.summary}</div>
											<div className="small text-muted">
												<strong>User:</strong> {formatUserLabel(item)}
												{item.user_id ? ` · #${item.user_id}` : ''}
											</div>
											<div className="small text-muted">
												{item.device_type || 'unknown device'}
												{item.browser ? ` · ${item.browser}` : ''}
												{item.locale ? ` · ${item.locale}` : ''}
												{item.is_logged_in ? ' · logged in' : ' · anonymous'}
											</div>
										</div>
										<div className="flex-shrink-0">
											<button
												type="button"
												className="btn btn-sm btn-outline-secondary"
												onClick={() => setExpandedId(expanded ? null : item.id)}
											>
												{expanded ? 'Hide details' : 'Details'}
											</button>
										</div>
									</div>
									{expanded && (
										<div className="mt-3">
											<div className="small text-muted mb-1">Session: {item.session_id}</div>
											<pre
												className="small bg-light border rounded p-3 mb-0 text-break"
												style={{ maxHeight: 320, overflow: 'auto' }}
											>
												{JSON.stringify(item.properties ?? {}, null, 2)}
											</pre>
										</div>
									)}
								</li>
							);
						})}
					</ul>
				</div>
			)}

			{total > limit && (
				<div className="d-flex justify-content-between align-items-center mt-3">
					<button
						type="button"
						className="btn btn-sm btn-outline-secondary"
						disabled={!canPrev || loading}
						onClick={() => setOffset((value) => Math.max(0, value - limit))}
					>
						Previous
					</button>
					<button
						type="button"
						className="btn btn-sm btn-outline-secondary"
						disabled={!canNext || loading}
						onClick={() => setOffset((value) => value + limit)}
					>
						Next
					</button>
				</div>
			)}
		</>
	);
}
